import { useState, useMemo } from 'react';
import { Copy, Check, GripVertical } from 'lucide-react';
import { useDrag } from 'react-dnd';
import { cn } from '@/lib/utils';
import { DataTypeIcon, detectDataType, type DataType } from './data-type-icon';
import { Button } from './button';
import { toast } from 'sonner';
import { DragItem } from '@/contexts/DragDropContext';

export interface TreeNode {
  key: string;
  value: any;
  type: DataType;
  path: string;
  children?: TreeNode[];
  isExpandable: boolean;
  itemCount?: number;
}

interface TreeViewProps {
  data: any;
  defaultExpanded?: number; // Number of levels to expand by default
  showTypes?: boolean;
  showCopyButtons?: boolean;
  showPath?: boolean;
  maxDepth?: number;
  className?: string;
  searchQuery?: string;
  onNodeClick?: (path: string, value: any) => void;
  // Drag and drop props
  draggable?: boolean;
  nodeId?: string;
  nodeName?: string;
  sourceHandle?: string;
  /**
   * When true, drag items represent "current item" in an iterating node.
   * Generates $node["X"].output.item.field expressions.
   */
  isVirtualItem?: boolean;
}

interface TreeNodeComponentProps {
  node: TreeNode;
  level: number;
  defaultExpanded: number;
  showTypes: boolean;
  showCopyButtons: boolean;
  showPath: boolean;
  maxDepth: number;
  searchQuery?: string;
  onNodeClick?: (path: string, value: any) => void;
  // Drag and drop props
  draggable?: boolean;
  nodeId?: string;
  nodeName?: string;
  sourceHandle?: string;
  isVirtualItem?: boolean;
}

// Maximum array items to render to prevent browser crashes
const MAX_ARRAY_ITEMS_TO_RENDER = 100;

/**
 * Builds a tree structure from any data
 */
function buildTree(data: any, path: string = '', maxDepth: number = Infinity, currentDepth: number = 0): TreeNode[] {
  if (currentDepth >= maxDepth) {
    return [];
  }

  if (data === null || data === undefined) {
    return [{
      key: '',
      value: data,
      type: data === null ? 'null' : 'undefined',
      path,
      isExpandable: false,
      children: []
    }];
  }

  if (Array.isArray(data)) {
    const totalItems = data.length;
    const itemsToRender = data.slice(0, MAX_ARRAY_ITEMS_TO_RENDER);
    const nodes = itemsToRender.map((item, index) => {
      const itemPath = `${path}[${index}]`;
      const type = detectDataType(item);
      const isExpandable = type === 'object' || type === 'array';

      return {
        key: `[${index}]`,
        value: item,
        type,
        path: itemPath,
        isExpandable,
        itemCount: isExpandable ? (Array.isArray(item) ? item.length : Object.keys(item || {}).length) : undefined,
        children: isExpandable ? buildTree(item, itemPath, maxDepth, currentDepth + 1) : []
      };
    });

    // Add a "truncated" indicator if array was limited
    if (totalItems > MAX_ARRAY_ITEMS_TO_RENDER) {
      nodes.push({
        key: `... and ${totalItems - MAX_ARRAY_ITEMS_TO_RENDER} more items`,
        value: `Array contains ${totalItems} items total. Showing first ${MAX_ARRAY_ITEMS_TO_RENDER}.`,
        type: 'string' as DataType,
        path: `${path}[truncated]`,
        isExpandable: false,
        itemCount: 0,
        children: []
      });
    }

    return nodes;
  }

  if (typeof data === 'object') {
    // Filter out duplicate node ID keys when label keys exist
    // Node IDs come in two formats:
    // 1. Timestamp-based: "if-1770370633856-if6ylhelz", "merge-1770370742201"
    // 2. Random alphanumeric: "SyRhWBGQJiUefH_RofmeC", "tuquJrglc2eEfPcfQ3wzb"
    // Labels look like: "If", "Merge", "Loop", "Code" (typically shorter, readable words)
    const entries = Object.entries(data);

    // Pattern for node IDs:
    // 1. Contains a sequence of 10+ digits (timestamp)
    // 2. OR looks like a random ID (nanoid-style)
    const looksLikeNodeId = (key: string) => {
      // Timestamp-based IDs (e.g., "if-1770370633856-if6ylhelz", "merge-1770370742201")
      if (/\d{10,}/.test(key)) return true;

      // Random alphanumeric IDs (nanoid-style, e.g., "zmY3Yv4ae8J8twqvCDwr9")
      // These are typically 21 chars, mixed case, alphanumeric only
      // Key distinguishing feature: they have a high mix of case changes that don't follow
      // camelCase or PascalCase patterns (random distribution of upper/lower)
      if (key.length >= 18 && /^[a-zA-Z0-9_-]+$/.test(key)) {
        // Count case transitions (e.g., "aB" or "Ba")
        let transitions = 0;
        for (let i = 1; i < key.length; i++) {
          const prevIsLower = /[a-z]/.test(key[i - 1]);
          const prevIsUpper = /[A-Z]/.test(key[i - 1]);
          const currIsLower = /[a-z]/.test(key[i]);
          const currIsUpper = /[A-Z]/.test(key[i]);
          if ((prevIsLower && currIsUpper) || (prevIsUpper && currIsLower)) {
            transitions++;
          }
        }
        // Random IDs have many transitions (5+), readable names have few (0-2)
        // "zmY3Yv4ae8J8twqvCDwr9" has ~6 transitions, "AIAgent" has 1
        if (transitions >= 4) return true;
      }

      // IDs with underscores/hyphens AND mixed case (excluding snake_case)
      // e.g., "SyRhWBGQJiUefH_RofmeC"
      const hasMixedCase = /[a-z]/.test(key) && /[A-Z]/.test(key);
      if (key.length >= 15 && /^[a-zA-Z0-9_-]+$/.test(key) && /[_-]/.test(key) && hasMixedCase) return true;

      return false;
    };

    // Count ID-like keys and non-ID keys
    const idKeys = entries.filter(([key]) => looksLikeNodeId(key));
    const nonIdKeys = entries.filter(([key]) => !looksLikeNodeId(key));

    // Only filter out ID keys if we have BOTH types (meaning duplicates exist)
    const shouldFilter = idKeys.length > 0 && nonIdKeys.length > 0;

    const filteredEntries = shouldFilter
      ? entries.filter(([key]) => !looksLikeNodeId(key)) // Keep only non-ID keys (labels)
      : entries; // Keep all if no duplicates

    return filteredEntries.map(([key, value]) => {
      const itemPath = path ? `${path}.${key}` : key;
      const type = detectDataType(value);
      const isExpandable = type === 'object' || type === 'array';

      return {
        key,
        value,
        type,
        path: itemPath,
        isExpandable,
        itemCount: isExpandable ? (Array.isArray(value) ? value.length : Object.keys(value || {}).length) : undefined,
        children: isExpandable ? buildTree(value, itemPath, maxDepth, currentDepth + 1) : []
      };
    });
  }

  return [{
    key: '',
    value: data,
    type: detectDataType(data),
    path,
    isExpandable: false,
    children: []
  }];
}

/**
 * Formats a byte size for human-readable display
 */
function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Checks if a string looks like base64 encoded binary content
 * More strict checks to avoid false positives with CSV/text content
 */
function isBase64Content(value: string): boolean {
  // Check if it's a long string
  if (value.length < 100) return false;

  // Base64 should NOT contain common text characters like newlines, commas, spaces
  // Check first 200 chars for text indicators
  const sample = value.substring(0, 200);
  const hasTextIndicators = /[\n\r,\s:;{}[\]<>]/.test(sample);
  if (hasTextIndicators) return false;

  // Base64 pattern: only alphanumeric + /+ with optional = padding at end
  const base64Pattern = /^[A-Za-z0-9+/]+=*$/;
  // Check first 100 chars
  return base64Pattern.test(value.substring(0, 100));
}

// Maximum characters to display for long strings
const MAX_STRING_DISPLAY_LENGTH = 500;

/**
 * Formats a value for display
 */
function formatValue(value: any, type: DataType, fieldKey?: string): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (type === 'string') {
    const strValue = String(value);

    // Handle very large strings (likely binary data)
    if (strValue.length > MAX_STRING_DISPLAY_LENGTH) {
      const byteSize = new Blob([strValue]).size;

      // Check if it looks like base64 content (only use field name hint for 'base64' specifically)
      const isLikelyBase64 = isBase64Content(strValue) || fieldKey?.toLowerCase().includes('base64');
      if (isLikelyBase64) {
        // Estimate original binary size (base64 is ~4/3 the size of original)
        const estimatedBinarySize = Math.floor(byteSize * 0.75);
        return `[Binary content: ~${formatByteSize(estimatedBinarySize)} encoded as ${formatByteSize(byteSize)} base64]`;
      }

      // For other large strings (like decoded text content), show preview with size
      const preview = strValue.substring(0, 100).replace(/\n/g, '\\n');
      return `"${preview}..." [${formatByteSize(byteSize)} total]`;
    }

    return `"${strValue}"`;
  }
  if (type === 'boolean') return value ? 'true' : 'false';
  if (type === 'number') return String(value);
  if (type === 'date') return value instanceof Date ? value.toISOString() : value;
  if (type === 'url') return value;
  return '';
}

/**
 * Check if a node or its children match the search query
 */
function nodeMatchesSearch(node: TreeNode, searchQuery: string): boolean {
  if (!searchQuery) return true;

  const query = searchQuery.toLowerCase();

  // Check if key matches
  if (node.key.toLowerCase().includes(query)) return true;

  // Check if value matches (for leaf nodes)
  if (!node.isExpandable) {
    const valueStr = formatValue(node.value, node.type, node.key).toLowerCase();
    if (valueStr.includes(query)) return true;
  }

  // Check if any children match
  if (node.children && node.children.length > 0) {
    return node.children.some(child => nodeMatchesSearch(child, searchQuery));
  }

  return false;
}

/**
 * Filter tree nodes based on search query
 */
function filterTree(nodes: TreeNode[], searchQuery: string): TreeNode[] {
  if (!searchQuery) return nodes;
  
  return nodes
    .map(node => {
      // If node matches, include it with all children
      if (nodeMatchesSearch(node, searchQuery)) {
        const filtered: TreeNode = {
          ...node,
          children: node.children ? filterTree(node.children, searchQuery) : undefined
        };
        return filtered;
      }
      return null;
    })
    .filter((node): node is TreeNode => node !== null);
}

/**
 * Count total matches in tree nodes
 */
function countMatches(nodes: TreeNode[], searchQuery: string): number {
  if (!searchQuery) return 0;

  let count = 0;
  const query = searchQuery.toLowerCase();

  for (const node of nodes) {
    // Check if key matches
    if (node.key.toLowerCase().includes(query)) {
      count++;
    }

    // Check if value matches (for leaf nodes)
    if (!node.isExpandable) {
      const valueStr = formatValue(node.value, node.type, node.key).toLowerCase();
      if (valueStr.includes(query) && !node.key.toLowerCase().includes(query)) {
        // Only count value match if key didn't already match
        count++;
      }
    }

    // Recursively count in children
    if (node.children && node.children.length > 0) {
      count += countMatches(node.children, searchQuery);
    }
  }

  return count;
}

/**
 * Highlight matching text in a string
 */
function highlightText(text: string, searchQuery: string) {
  if (!searchQuery) return text;
  
  const query = searchQuery.toLowerCase();
  const lowerText = text.toLowerCase();
  const index = lowerText.indexOf(query);
  
  if (index === -1) return text;
  
  return (
    <>
      {text.substring(0, index)}
      <mark className="bg-yellow-300 dark:bg-yellow-600 text-foreground px-0.5 rounded">
        {text.substring(index, index + searchQuery.length)}
      </mark>
      {text.substring(index + searchQuery.length)}
    </>
  );
}

/**
 * TreeNodeComponent - Renders a single tree node
 */
function TreeNodeComponent({
  node,
  level,
  defaultExpanded,
  showTypes,
  showCopyButtons,
  showPath,
  maxDepth,
  searchQuery,
  onNodeClick,
  draggable = false,
  nodeId = 'unknown',
  nodeName = 'Unknown Node',
  sourceHandle,
  isVirtualItem = false
}: TreeNodeComponentProps) {
  // Auto-expand when searching or based on defaultExpanded
  const shouldAutoExpand = searchQuery ? true : level < defaultExpanded;
  const [isExpanded, setIsExpanded] = useState(shouldAutoExpand);
  const [isCopied, setIsCopied] = useState(false);

  // Determine if this node can be dragged
  // Allow dragging leaf nodes AND array/object nodes (even if they have children)
  const isDraggable = draggable && (!node.isExpandable || node.type === 'array' || node.type === 'object');

  // Setup drag
  const [{ isDragging }, dragRef, previewRef] = useDrag<DragItem, unknown, { isDragging: boolean }>({
    type: 'schema-field',
    item: () => ({
      type: 'schema-field',
      nodeId,
      nodeName,
      path: node.path,
      fieldType: node.type,
      value: node.value,
      sourceHandle,
      isVirtualItem,
    }),
    canDrag: isDraggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // For copy, always use the raw value (not truncated)
      let textToCopy: string;
      if (node.isExpandable) {
        textToCopy = JSON.stringify(node.value, null, 2);
      } else if (typeof node.value === 'string') {
        textToCopy = node.value;
      } else {
        textToCopy = formatValue(node.value, node.type, node.key);
      }

      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleClick = () => {
    if (node.isExpandable) {
      setIsExpanded(!isExpanded);
    }
    onNodeClick?.(node.path, node.value);
  };

  const paddingLeft = level * 16 + 8;

  return (
    <div className="select-none" ref={previewRef as any}>
      <div
        ref={isDraggable ? (dragRef as any) : null}
        className={cn(
          "flex items-start gap-2 py-1 px-2 hover:bg-accent/50 rounded-sm group transition-colors min-w-0 overflow-hidden",
          node.isExpandable && "font-medium",
          isDraggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
          isDragging && "opacity-50"
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={handleClick}
        title={showPath ? node.path : undefined}
      >
        {/* Data Type Icon (rotates when expandable) */}
        {showTypes && (
          <div className="flex items-center gap-1">
            <div 
              className={cn(
                "flex-shrink-0 transition-transform duration-200",
                node.isExpandable && isExpanded && "rotate-90"
              )}
            >
              <DataTypeIcon type={node.type} size={14} />
            </div>
            {isDraggable && (
              <GripVertical className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>
        )}

        {/* Key */}
        {node.key && (
          <span className="text-sm font-mono text-foreground flex-shrink-0">
            {highlightText(node.key, searchQuery || '')}
          </span>
        )}

        {/* Separator */}
        {node.key && !node.isExpandable && (
          <span className="text-muted-foreground flex-shrink-0">:</span>
        )}

        {/* Value or Item Count */}
        {!node.isExpandable ? (
          <span className={cn(
            "text-sm font-mono flex-1 min-w-0 break-all whitespace-pre-wrap overflow-hidden",
            node.type === 'string' && "text-green-600 dark:text-green-400",
            node.type === 'number' && "text-blue-600 dark:text-blue-400",
            node.type === 'boolean' && "text-purple-600 dark:text-purple-400",
            (node.type === 'null' || node.type === 'undefined') && "text-gray-400 italic"
          )}>
            {highlightText(formatValue(node.value, node.type, node.key), searchQuery || '')}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {node.itemCount !== undefined && (
              <>
                {node.type === 'array' ? `[${node.itemCount}]` : `{${node.itemCount}}`}
              </>
            )}
          </span>
        )}

        {/* Copy Button */}
        {showCopyButtons && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            onClick={handleCopy}
          >
            {isCopied ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>

      {/* Children */}
      {node.isExpandable && isExpanded && node.children && (
        <div>
          {node.children.map((child, index) => (
            <TreeNodeComponent
              key={`${child.path}-${index}`}
              node={child}
              level={level + 1}
              defaultExpanded={defaultExpanded}
              showTypes={showTypes}
              showCopyButtons={showCopyButtons}
              showPath={showPath}
              maxDepth={maxDepth}
              searchQuery={searchQuery}
              onNodeClick={onNodeClick}
              draggable={draggable}
              nodeId={nodeId}
              nodeName={nodeName}
              sourceHandle={sourceHandle}
              isVirtualItem={isVirtualItem}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Count matches in data - exported for external use
 */
export function countTreeMatches(data: any, searchQuery: string, maxDepth: number = Infinity): number {
  if (!searchQuery) return 0;
  const tree = buildTree(data, '', maxDepth);
  return countMatches(tree, searchQuery);
}

/**
 * TreeView Component - Displays hierarchical data in a collapsible tree structure
 */
export function TreeView({
  data,
  defaultExpanded = 1,
  showTypes = true,
  showCopyButtons = true,
  showPath = true,
  maxDepth = Infinity,
  className,
  searchQuery,
  onNodeClick,
  draggable = false,
  nodeId,
  nodeName,
  sourceHandle,
  isVirtualItem = false
}: TreeViewProps) {
  const tree = useMemo(() => buildTree(data, '', maxDepth), [data, maxDepth]);
  const filteredTree = useMemo(() => filterTree(tree, searchQuery || ''), [tree, searchQuery]);

  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return (
      <div className={cn("flex items-center justify-center p-8 text-muted-foreground", className)}>
        <p className="text-sm">No data to display</p>
      </div>
    );
  }

  if (searchQuery && filteredTree.length === 0) {
    return (
      <div className={cn("flex items-center justify-center p-8 text-muted-foreground", className)}>
        <p className="text-sm">No matches found for "{searchQuery}"</p>
      </div>
    );
  }

  return (
    <div className={cn("font-mono text-sm overflow-x-hidden overflow-y-auto", className)}>
      {filteredTree.map((node, index) => (
        <TreeNodeComponent
          key={`${node.path}-${index}`}
          node={node}
          level={0}
          defaultExpanded={defaultExpanded}
          showTypes={showTypes}
          showCopyButtons={showCopyButtons}
          showPath={showPath}
          maxDepth={maxDepth}
          searchQuery={searchQuery}
          onNodeClick={onNodeClick}
          draggable={draggable}
          nodeId={nodeId}
          nodeName={nodeName}
          sourceHandle={sourceHandle}
          isVirtualItem={isVirtualItem}
        />
      ))}
    </div>
  );
}
